import {
  type AuthenticationStore,
  type AuthenticationStatus,
} from '@hurdl/authentication/frontend';

import { type router as PanelRouter } from '../routes.js';

/**
 * The route the Sole_Factor_Login renders at, and the one the panel lands a signed-in
 * operator on. The guard moves between exactly these two — it never chooses a deeper
 * destination, which is the login page's and the application's decision (R9.3), not the
 * gate's.
 */
const LOGIN_PATH = '/login';
const HOME_PATH = '/';

/**
 * The route the first key is enrolled at (ADR 0050).
 *
 * It is reached before any account can sign in — the bootstrap administrator holds a
 * one-time ticket and no session — so an anonymous operator must be let through to it
 * rather than bounced to a login they cannot yet pass. The ticket in the address is the
 * credential; the route is anonymous by design, exactly as the Sole_Factor_Login is.
 */
const ENROL_PATH = '/enrol';

/**
 * The paths an anonymous operator may rest on without being sent to the login.
 *
 * The login itself, and the enrol route, which precedes the first sign-in. Every other
 * path requires a session; an anonymous operator on one is bounced to the login.
 */
const ANONYMOUS_PATHS: ReadonlyArray<string> = [LOGIN_PATH, ENROL_PATH];

/**
 * The build-time login posture, read from Vite's `import.meta.env`.
 *
 * `applications/admin-panel/dev.ts` sets `VITE_ADMIN_OPEN_LOGIN` from the chosen `--db`
 * target — `'true'` for the frictionless in-memory and `--db local` runs, `'false'` for
 * the cloud targets that require a key-authenticated operator — and Vite exposes a
 * `VITE_`-prefixed value to the browser. It is read `=== 'true'` and off for anything
 * else, exactly as `main.ts` reads the backend's `ADMIN_OPEN_LOGIN`, so the two postures
 * are the one decision. Absent (a build with no flag set, such as `pnpm build` without
 * the dispatcher) the login is required, which is the safe default: a gate that opened
 * itself where it was told nothing would be the anonymous surface the panel forbids.
 *
 * Read through a narrow accessor rather than a project-wide `vite/client` types addition:
 * the one value the guard needs does not justify widening every module's `import.meta`.
 */
function loginIsOpen(): boolean {
  const environment = (
    import.meta as unknown as {
      readonly env?: Readonly<Record<string, string | undefined>>;
    }
  ).env;
  return environment?.VITE_ADMIN_OPEN_LOGIN === 'true';
}

/**
 * The two statuses the guard acts on. `unknown` and `authenticating` are the session
 * still being recovered on mount (`AuthenticationStore.initialise`), and the guard waits
 * them out rather than bouncing an operator to the login page a held cookie is about to
 * restore a session from.
 */
function isSettled(status: AuthenticationStatus): boolean {
  return status === 'authenticated' || status === 'anonymous';
}

/**
 * Routes an un-signed-in operator to the Sole_Factor_Login, and a signed-in one off it,
 * where the panel requires a key (admin-panel R9.3, R9.6).
 *
 * **Why this is wired here, outside the component tree, rather than in the shell.** The
 * guard needs both the authentication store and the router, and the one place that holds
 * both is `main.tsx`, where the application is composed — not `layout.tsx`, which the
 * Cosmos Fixtures and the per-page appearance suite mount with no `AuthenticationProvider`
 * above them (a `useAuthentication` call in the shell would throw there, breaking the
 * appearance check invariant 5 rests on). Subscribing to the store and the router keeps
 * the shell a pure presentation of a route and leaves every Fixture untouched: the guard
 * is part of the real application's composition and nothing the playground runs.
 *
 * **It is inert when the login is open.** The frictionless in-memory and `--db local`
 * runs resolve a development operator with no key, so the store stays `anonymous` (there
 * being no session to recover) and a guard that redirected on `anonymous` would strand
 * that run on a login it never needed. Where the flag is on the guard installs nothing
 * and returns a no-op teardown.
 *
 * **It waits out session recovery.** On mount the store is `unknown`, then
 * `authenticating` while it tries the held refresh cookie; the guard moves only once the
 * status settles to `authenticated` or `anonymous`, so a reload that will restore a
 * session does not flash the login page first.
 *
 * It evaluates on every store change and every navigation, because either can be what
 * makes a redirect due: the session lapsing to `anonymous` on a page that requires one,
 * or a navigation to a guarded page while already `anonymous`. A signed-in operator who
 * reaches a pre-sign-in page — `/login` or the first-key `/enrol` route — directly is
 * sent home, so neither is ever the resting place of an authenticated session. The enrol
 * route is anonymous-reachable because it precedes the first sign-in (ADR 0050): the
 * bootstrap administrator holds a one-time ticket and no session, so bouncing them to a
 * login they cannot yet pass would strand the bootstrap.
 *
 * @returns a teardown that removes both subscriptions, for the caller to call on unmount.
 */
export function installAuthenticationGuard(
  store: AuthenticationStore,
  router: typeof PanelRouter,
): () => void {
  // Open login: the gate is not wanted, so nothing is installed.
  if (loginIsOpen()) {
    return () => undefined;
  }

  const evaluate = (): void => {
    const { status } = store.getSnapshot();
    if (!isSettled(status)) {
      return;
    }

    const path = router.state.location.pathname;
    const authenticated = status === 'authenticated';
    const anonymousReachable = ANONYMOUS_PATHS.includes(path);

    if (!authenticated && !anonymousReachable) {
      void router.navigate({ to: LOGIN_PATH });
      return;
    }

    // A signed-in operator has no business on a pre-sign-in page — the login or the
    // enrol route — so they are sent home, which keeps neither the resting place of an
    // authenticated session.
    if (authenticated && anonymousReachable) {
      void router.navigate({ to: HOME_PATH });
    }
  };

  const unsubscribeStore = store.subscribe(evaluate);
  const unsubscribeRouter = router.subscribe('onResolved', evaluate);

  // One evaluation now, in case the status has already settled — a store handed in
  // already `anonymous`, or a first paint that lands on a guarded path.
  evaluate();

  return () => {
    unsubscribeStore();
    unsubscribeRouter();
  };
}
